import type { Optional } from '@festina-life/core'
import { type QueryKey, useQuery } from '@tanstack/react-query'

import { useFPlusFormattedExpressionsContext } from '../../context/fPlusFormattedExpressionsContext'

/**
 * The line width both sides of a diff are formatted to.
 *
 * It is a fixed number rather than the width of whatever the diff happens to be
 * shown in, so that resizing that container can never reflow one side and
 * manufacture a change the author never made. It is narrow enough that each of
 * the two side-by-side columns can show a line without wrapping.
 */
const FPLUS_DIFF_WIDTH = 80

type Sides = {
  compare: Optional<string>
  current: Optional<string>
}

const formattedDiffSidesKey = ({ compare, current }: Sides): QueryKey => [
  'fplus/diff/formatted-sides',
  FPLUS_DIFF_WIDTH,
  compare,
  current,
]

/**
 * Lays both sides of a comparison out to the same line width, so that only real
 * changes show up rather than differences in layout.
 */
export const useFormattedFPlusDiffSides = ({ compare, current }: Sides) => {
  const { formatDirectly } = useFPlusFormattedExpressionsContext()
  const { data, isError } = useQuery({
    queryKey: formattedDiffSidesKey({ compare, current }),
    queryFn: () =>
      formatDirectly([compare ?? '', current ?? ''], FPLUS_DIFF_WIDTH),
    staleTime: Infinity,
    retry: false,
  })
  // Better an unformatted comparison, overstating what changed, than none.
  const sides = data ?? (isError ? [compare ?? '', current ?? ''] : null)

  return {
    compare: compare ? sides?.at(0) : undefined,
    current: current ? sides?.at(1) : undefined,
    /** Whether both sides are in hand and can be compared. */
    isResolved: !!sides,
  }
}
